import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import '@ant-design/compatible/assets/index.css';
import { Modal, Input, Alert, Select, Switch, Form, Spin, Row, Radio, Popover } from 'antd';
import { connect } from 'react-redux';
import {
	compareStrings,
	hasAccessToAllLocations,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import BuyerRegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import { fetchUserForBuyer } from '../../thunks/users_thunks';
import { BUYER_ADMIN_ONLY } from '../roles/roles';
import { FILTER_CONFIG_NAMES } from '../../utils/DataConstants';
import { BUYER_CONTACTS_CRUD_ACTION_CREATORS } from '../../actions/buyer_contacts_actions';
import OWForm from '../ow_form/OWForm';
import BrandSelection from '../brand_selection/BrandSelection';
import { InfoCircleOutlined } from '@ant-design/icons';
import { EPA_CERTIFICATION_TYPE_OPTIONS } from '../../utils/RefrigerantUtils';

const FormItem = Form.Item;

const BuyerAdminRoleUpdateForm: FC<any> = ({
	visible,
	onCancel,
	onSubmit,
	formData,
	createErrors,
	updateErrors,
	locations,
	isLocationChange,
	fetchUser,
	creating,
	updating,
}) => {
	const [form] = Form.useForm();

	const { setFieldsValue } = form;

	const [loading, setLoading] = useState(false);
	const [hasBrands, setHasBrands] = useState(false);
	const email = useMemo(
		() => (visible ? nullSafeGet('contact.email', formData) : null),
		[formData, visible]
	);

	const roles = Form.useWatch('roles', form);
	const includesBuyerAdmin = BUYER_ADMIN_ONLY.has(roles);

	const hasAccessToAllLocations = Form.useWatch('hasAccessToAllLocations', form);
	const byLocationOrBrand = Form.useWatch('byLocationOrBrand', form);

	const toggleAccessType = useMemo(
		() => (value) => {
			form.setFieldsValue({
				locationIds: [],
				brandIds: [],
				byLocationOrBrand: value,
			});
		},
		[]
	);

	const refreshUser = useCallback(() => {
		setLoading(true);
		fetchUser(email)
			.then((user) => {
				const locationOrBrand =
					nullSafeGetOrElse('brandIds', user, []).length > 0 ? 'brand' : 'location';
				setFieldsValue({
					email: nullSafeGet('contact.email', user),
					nameGiven: nullSafeGet('contact.nameGiven', user),
					nameFamily: nullSafeGet('contact.nameFamily', user),
					roles:
						nullSafeGetOrElse('role', user, []).length > 0
							? nullSafeGet('role', user)[0]
							: 'BUYER_STORE_ASSOCIATE',
					locationIds: nullSafeGetOrElse('locationIds', user, []),
					hasAccessToAllLocations: !!user.hasAccessToAllLocations,
					isSharedContact: nullSafeGetOrElse('contact.isSharedContact', user, false),
					readOnlyAccess: nullSafeGetOrElse('contact.readOnlyAccess', user, false),
					brandIds: nullSafeGetOrElse('brandIds', user, []),
					byLocationOrBrand: locationOrBrand,
					// Same clobber hazard as the supplier modal: `handleSubmit` forwards every registered
					// field, so a cert field that opens blank writes the blank back and silently decertifies
					// the user on the next unrelated save. Seeding from the user's nested `contact` — where
					// `fetchUserForBuyer` returns them — is what keeps the round trip lossless.
					epaCertificationType: nullSafeGet('contact.epaCertificationType', user),
					epaCertificationNumber: nullSafeGet('contact.epaCertificationNumber', user),
				});
			})
			.finally(() => setLoading(false));
	}, [email, fetchUser, setFieldsValue]);

	useEffect(() => {
		email && refreshUser();
	}, [email, fetchUser, refreshUser]);
	const handleSubmit = React.useCallback(
		(values) => {
			const hasAccessToAllLocations =
				BUYER_ADMIN_ONLY.has(values.roles) ||
				nullSafeGetOrElse('hasAccessToAllLocations', values, false);
			const brandIds =
				hasAccessToAllLocations ||
				compareStrings(nullSafeGetOrElse('byLocationOrBrand', values, 'location'), 'location')
					? []
					: values.brandIds;
			const locationIds =
				hasAccessToAllLocations ||
				compareStrings(nullSafeGetOrElse('byLocationOrBrand', values, 'brand'), 'brand')
					? []
					: values.locationIds;
			const updatedValues = {
				...values,
				hasAccessToAllLocations: hasAccessToAllLocations,
				brandIds: brandIds,
				locationIds: locationIds,
				// Same as the supplier modal: an absent cert key means "keep the stored value" on every
				// contact write path, and JSON.stringify erases undefined — so clearing a lapsed
				// certification has to be spelled out as an explicit null or it silently does nothing.
				epaCertificationType:
					values.epaCertificationType === undefined ? null : values.epaCertificationType,
				epaCertificationNumber:
					values.epaCertificationNumber === undefined ? null : values.epaCertificationNumber,
			};

			onSubmit && onSubmit(updatedValues);
		},
		[onSubmit]
	);

	return (
		<Modal
			visible={visible}
			width={600}
			title={'Edit User Information'}
			okText={'Update'}
			onCancel={onCancel}
			onOk={form.submit}
			closable={true}
			destroyOnClose={true}
			confirmLoading={creating || updating}
		>
			{loading ? (
				<Spin />
			) : (
				<OWForm
					form={form}
					layout="vertical"
					className="supplierForm"
					preserve={false}
					initialValues={{}}
					onFinish={handleSubmit}
					actionCreators={BUYER_CONTACTS_CRUD_ACTION_CREATORS}
				>
					{createErrors.length > 0 ? (
						<FormItem>
							<Alert message={createErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{updateErrors.length > 0 ? (
						<FormItem>
							<Alert message={updateErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					<FormItem label="Email" name={'email'}>
						<Input defaultValue={email} style={{ maxWidth: 300 }} disabled={true} />
					</FormItem>
					<FormItem
						label={'Name'}
						className={'flex flex-row'}
						required={true}
						rules={[
							{
								required: true,
								message: 'Please input name.',
							},
						]}
					>
						<div className={'flex flex-row'} style={{ marginBottom: -24 }}>
							<FormItem name={'nameGiven'}>
								<Input
									placeholder="Given name"
									style={{ maxWidth: 200 }}
									disabled={false}
									defaultValue={nullSafeGet('contact.nameGiven', formData)}
								/>
							</FormItem>
							<FormItem name={'nameFamily'}>
								<Input
									defaultValue={nullSafeGet('contact.nameFamily', formData)}
									placeholder="Family name"
									style={{ maxWidth: 200, marginLeft: 8 }}
									disabled={false}
								/>
							</FormItem>
						</div>
					</FormItem>

					{/*
						The buyer twin of the supplier modal's EPA 608 block, in the same position and for the
						same reason: contact-record attributes sit with the name, above the role/location
						scoping below.

						Required on the buyer side too — buyers log refrigerant events on their in-house
						appliances, and a buyer admin is usually the one holding the tech's 608 card. Rule (a)
						of the event's acceptance test does not distinguish the two personas.
					*/}
					<>
						<FormItem label="EPA Certification Type" name="epaCertificationType">
							<Select placeholder="Select certification type" allowClear style={{ maxWidth: 300 }}>
								{EPA_CERTIFICATION_TYPE_OPTIONS.map((option) => (
									<Select.Option value={option.value} key={option.value}>
										{option.label}
									</Select.Option>
								))}
							</Select>
						</FormItem>
						<FormItem label="EPA Certification Number" name="epaCertificationNumber">
							<Input placeholder="Certification number" style={{ maxWidth: 300 }} />
						</FormItem>
					</>

					<FormItem
						label="Roles"
						name="roles"
						required={true}
						rules={[
							{
								required: true,
								message: 'Please select a role',
							},
						]}
					>
						<Select
							disabled={isLocationChange ? isLocationChange : false}
							style={{ maxWidth: 200 }}
							defaultValue={
								nullSafeGet('role', formData) === undefined ? 'BUYER_USER' : formData.role[0]
							}
							onChange={(role) => {
								form.setFieldsValue({
									hasAccessToAllLocations: BUYER_ADMIN_ONLY.has(role),
								});
							}}
						>
							<Select.Option value="BUYER_ADMIN">Admin</Select.Option>
							<Select.Option value="BUYER_USER">Manager</Select.Option>
							<Select.Option value="BUYER_STORE_ASSOCIATE">Store Associate</Select.Option>
						</Select>
					</FormItem>
					<FormItem
						name="hasAccessToAllLocations"
						valuePropName="checked"
						required={true}
						hidden={includesBuyerAdmin}
					>
						<Row>
							<Switch
								checked={!!form.getFieldValue('hasAccessToAllLocations')}
								onChange={(checked) => form.setFieldsValue({ hasAccessToAllLocations: !!checked })}
							/>
							<label style={{ marginLeft: 8 }}>Select All Locations</label>
						</Row>
					</FormItem>
					<FormItem
						name="byLocationOrBrand"
						required={!hasAccessToAllLocations && hasBrands}
						hidden={hasAccessToAllLocations || !hasBrands}
						label={'Restrict Access By'}
					>
						<Radio.Group
							onChange={(e) => {
								toggleAccessType(e.target.value);
							}}
							value={form.getFieldValue('byLocationOrBrand')}
						>
							<Radio.Button value="location">Location</Radio.Button>
							<Radio.Button value="brand">Brand</Radio.Button>
						</Radio.Group>
					</FormItem>
					<FormItem
						name="brandIds"
						hidden={
							includesBuyerAdmin ||
							hasAccessToAllLocations ||
							!compareStrings(byLocationOrBrand, 'brand')
						}
						required={true}
						label={
							<Row>
								<span>Brands</span>
								<Popover
									placement={'topLeft'}
									content={
										'Selecting a brand will give the user access to all locations in that brand.'
									}
								>
									<InfoCircleOutlined
										style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}
									/>
								</Popover>
							</Row>
						}
						rules={[
							{
								type: 'array',
								required: !hasAccessToAllLocations && compareStrings(byLocationOrBrand, 'brand'),
								message: 'Please select a brand',
							},
						]}
					>
						<BrandSelection setHasBrands={setHasBrands} />
					</FormItem>
					<FormItem
						name="locationIds"
						hidden={
							includesBuyerAdmin ||
							hasAccessToAllLocations ||
							!compareStrings(byLocationOrBrand, 'location')
						}
						required={true}
						label={'Locations'}
						rules={[
							{
								type: 'array',
								required: !hasAccessToAllLocations && compareStrings(byLocationOrBrand, 'location'),
								message: 'Please select a location',
							},
						]}
					>
						<BuyerRegionsLocationsTreeSelect
							mode="multiple"
							noIsActiveFilter
							disabled={hasAccessToAllLocations}
							filtersSlice={locations}
							filtersValueAccessor={() => form.getFieldValue(FILTER_CONFIG_NAMES.LOCATION)}
							targetCollectionName={'buyerUsersLocations'}
						/>
					</FormItem>
					<div className="flex flex-row items-baseline">
						<FormItem name="isSharedContact" valuePropName="checked">
							<Switch />
						</FormItem>
						<label className="ml-2">Shared Account</label>
					</div>
					{!includesBuyerAdmin && (
						<div className="flex flex-row items-baseline">
							<FormItem name="readOnlyAccess" valuePropName="checked">
								<Switch />
							</FormItem>
							<label className="ml-2">Read Only Access</label>
						</div>
					)}
				</OWForm>
			)}
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	createErrors: state.buyer_contacts.createErrors,
	updateErrors: state.buyer_contacts.updateErrors,
	creating: state.buyer_contacts.creating,
	updating: state.buyer_contacts.updating,
	currentUser: state.session.currentUser,
	formData: ownProps.formData,
	isLocationChange: ownProps.isLocationChange,

	buyerUsers: state.buyer_users,
	locations: state.locations,
	regions: state.regions,
});

const mapDispatchToProps = (dispatch) => ({
	fetchUser: (email) => dispatch(fetchUserForBuyer(email)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BuyerAdminRoleUpdateForm);
